/**
 * ทะเบียนคำสั่งและปุ่มลัด
 *
 * **ที่เดียวที่รู้ว่าปุ่มไหนทำอะไร** โค้ดในเกมถามเป็น "คำสั่ง" ไม่ใช่ "ปุ่ม"
 * เช่น `actionOf(e.code) === "attack"` ไม่ใช่ `e.code === "KeyF"`
 * ผู้เล่นจึงเปลี่ยนปุ่มได้ทุกปุ่มโดยไม่ต้องแก้โค้ดที่ใช้งานมันเลยสักบรรทัด
 *
 * ใช้ `event.code` ไม่ใช่ `event.key` จึงกดได้ตอนแป้นเป็นภาษาไทย
 * (`code` คือตำแหน่งปุ่มบนแป้น ไม่ใช่ตัวอักษรที่พิมพ์ออกมา)
 *
 * ส่วนใหญ่ในไฟล์นี้เป็น pure function จึงเทสต์ได้โดยไม่ต้องมี DOM
 */

export type ActionId =
  | "moveF" | "moveB" | "moveL" | "moveR"
  | "skill1" | "skill2" | "skill3" | "skill4" | "skill5"
  | "skill6" | "skill7" | "skill8" | "skill9" | "skill10"
  | "bag1" | "bag2" | "bag3" | "bag4" | "bag5"
  | "bag6" | "bag7" | "bag8" | "bag9" | "bag10"
  | "targetNext" | "attack" | "cancel" | "chat" | "pickAll"
  | "camReset" | "selfBar" | "selfArrow" | "soundToggle"
  | "winChar" | "winBag" | "winSkill" | "winShop" | "winRefine" | "winParty" | "winQuest"
  | "winSettings" | "winHelp" | "winKeys" | "winMap";

export interface ActionDef {
  id: ActionId;
  /** ชื่อที่แสดงในหน้าตั้งค่า */
  label: string;
  /** หัวข้อที่จัดกลุ่มในหน้าตั้งค่า */
  group: string;
  /** ปุ่มเริ่มต้น (`event.code`) */
  def: string;
  /**
   * ปุ่มสำรองที่ใช้ได้เสมอและเปลี่ยนไม่ได้ เช่นลูกศรและแป้นตัวเลข
   * มีไว้ให้คนถนัดคนละแบบใช้ได้โดยไม่กินช่องในหน้าตั้งค่า
   */
  also?: string[];
}

/** ลำดับในนี้คือลำดับที่แสดงในหน้าตั้งค่า */
export const ACTIONS: readonly ActionDef[] = [
  { id: "moveF", label: "เดินหน้า", group: "เคลื่อนที่", def: "KeyW", also: ["ArrowUp"] },
  { id: "moveB", label: "ถอยหลัง", group: "เคลื่อนที่", def: "KeyS", also: ["ArrowDown"] },
  { id: "moveL", label: "ไปทางซ้าย", group: "เคลื่อนที่", def: "KeyA", also: ["ArrowLeft"] },
  { id: "moveR", label: "ไปทางขวา", group: "เคลื่อนที่", def: "KeyD", also: ["ArrowRight"] },

  { id: "targetNext", label: "เลือกเป้าถัดไป", group: "ต่อสู้", def: "Tab" },
  { id: "attack", label: "สั่งตี / หยุดตี", group: "ต่อสู้", def: "KeyF" },
  { id: "pickAll", label: "เก็บไอเทมรอบตัวทั้งหมด", group: "ต่อสู้", def: "Space" },
  { id: "cancel", label: "ยกเลิก / ปิดหน้าต่าง", group: "ต่อสู้", def: "Escape" },

  ...["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"].map((n, i) => ({
    id: `skill${i + 1}` as ActionId,
    label: `แถบสกิลช่อง ${i + 1}`,
    group: "แถบลัด แถวบน",
    def: `Digit${n}`,
    also: [`Numpad${n}`],
  })),

  ...["KeyQ", "KeyE", "KeyT", "KeyY", "KeyZ", "KeyX", "KeyC", "KeyG", "KeyH", "KeyN"].map(
    (code, i) => ({
      id: `bag${i + 1}` as ActionId,
      label: `แถบลัดช่อง ${i + 1}`,
      group: "แถบลัด แถวล่าง",
      def: code,
    }),
  ),

  { id: "winChar", label: "ตัวละคร", group: "หน้าต่าง", def: "KeyI" },
  { id: "winBag", label: "กระเป๋า", group: "หน้าต่าง", def: "KeyB" },
  { id: "winSkill", label: "สกิล", group: "หน้าต่าง", def: "KeyK" },
  { id: "winShop", label: "ร้านค้า", group: "หน้าต่าง", def: "KeyP" },
  { id: "winRefine", label: "โรงตีเหล็ก", group: "หน้าต่าง", def: "KeyU" },
  { id: "winParty", label: "ปาร์ตี้", group: "หน้าต่าง", def: "KeyL" },
  { id: "winQuest", label: "เควส / ภารกิจ", group: "หน้าต่าง", def: "BracketLeft" },
  { id: "winSettings", label: "ตั้งค่า", group: "หน้าต่าง", def: "KeyO" },
  { id: "winHelp", label: "ปุ่มควบคุม", group: "หน้าต่าง", def: "Slash" },
  { id: "winKeys", label: "ตั้งค่าปุ่ม", group: "หน้าต่าง", def: "KeyJ" },
  { id: "winMap", label: "แผนที่", group: "หน้าต่าง", def: "KeyM" },

  { id: "chat", label: "พิมพ์แชท", group: "อื่น ๆ", def: "Enter", also: ["NumpadEnter"] },
  { id: "camReset", label: "รีเซ็ตกล้อง", group: "อื่น ๆ", def: "KeyR" },
  { id: "selfBar", label: "แถบเลือดใต้ตัวละคร", group: "อื่น ๆ", def: "KeyV" },
  { id: "selfArrow", label: "ลูกศรชี้ตัวเอง", group: "อื่น ๆ", def: "Comma" },
  { id: "soundToggle", label: "เปิด/ปิดเสียงเอฟเฟกต์", group: "อื่น ๆ", def: "F4" },
];

export type Bindings = Partial<Record<ActionId, string>>;

const KEYS_KEY = "wickhaven.keys";

export function defaultBindings(): Bindings {
  const out: Bindings = {};
  for (const a of ACTIONS) out[a.id] = a.def;
  return out;
}

/**
 * โหลดปุ่มที่ผู้เล่นตั้งไว้ ผสมกับค่าเริ่มต้น
 * คำสั่งที่เพิ่มเข้ามาใหม่หลังผู้เล่นเคยบันทึกไว้ จะได้ปุ่มเริ่มต้นของมันเอง
 */
export function loadBindings(): Bindings {
  const base = defaultBindings();
  try {
    const raw = localStorage.getItem(KEYS_KEY);
    if (!raw) return base;
    const saved = JSON.parse(raw) as Bindings;
    for (const a of ACTIONS) {
      const code = saved[a.id];
      // ว่าง = ผู้เล่นตั้งใจปลดปุ่มนั้นออก ต้องเคารพ ไม่ใช่เอาค่าเริ่มต้นมาทับ
      if (typeof code === "string") base[a.id] = code;
    }
    return base;
  } catch {
    return base;
  }
}

export function saveBindings(b: Bindings): void {
  try {
    localStorage.setItem(KEYS_KEY, JSON.stringify(b));
  } catch {
    // บันทึกไม่ได้ก็ยังเล่นรอบนี้ได้ แค่ไม่จำไว้รอบหน้า
  }
}

/**
 * ตารางค้นกลับจากปุ่มเป็นคำสั่ง
 * รวมปุ่มสำรองที่เปลี่ยนไม่ได้เข้าไปด้วย แต่ปุ่มที่ผู้เล่นตั้งเองชนะเสมอ
 */
export function buildLookup(b: Bindings): Map<string, ActionId> {
  const map = new Map<string, ActionId>();
  for (const a of ACTIONS) {
    for (const code of a.also ?? []) if (!map.has(code)) map.set(code, a.id);
  }
  for (const a of ACTIONS) {
    const code = b[a.id];
    if (code) map.set(code, a.id);
  }
  return map;
}

/** คำสั่งที่ใช้ปุ่มเดียวกัน (ไม่นับปุ่มสำรอง) คืนรายการ code ที่ซ้ำ */
export function duplicates(b: Bindings): string[] {
  const seen = new Map<string, number>();
  for (const a of ACTIONS) {
    const code = b[a.id];
    if (!code) continue;
    seen.set(code, (seen.get(code) ?? 0) + 1);
  }
  return [...seen.entries()].filter(([, n]) => n > 1).map(([code]) => code);
}

/**
 * ตั้งปุ่มให้คำสั่งหนึ่ง **ถ้าปุ่มนั้นมีคนใช้อยู่ จะถูกปลดออกจากคำสั่งเดิม**
 * เลือกปลดแทนการสลับ เพราะการสลับทำให้ผู้เล่นงงว่าปุ่มเก่าไปอยู่ไหน
 */
export function bind(b: Bindings, id: ActionId, code: string): Bindings {
  const next: Bindings = { ...b };
  for (const a of ACTIONS) {
    if (a.id !== id && next[a.id] === code) next[a.id] = "";
  }
  next[id] = code;
  return next;
}

/** ปุ่มที่ห้ามให้ตั้ง เพราะเบราว์เซอร์กินไปใช้เองหรือจะทำให้เล่นต่อไม่ได้ */
const FORBIDDEN = new Set([
  "F5", "F11", "F12", "MetaLeft", "MetaRight", "ContextMenu",
]);

export function canBind(code: string): boolean {
  return code.length > 0 && !FORBIDDEN.has(code);
}

/** ชื่อปุ่มที่อ่านรู้เรื่อง สำหรับแสดงในหน้าตั้งค่าและแผงช่วยเหลือ */
export function keyLabel(code: string | undefined): string {
  if (!code) return "—";
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code.startsWith("Numpad")) return `แป้นเลข ${code.slice(6)}`;
  if (code.startsWith("Arrow")) {
    const dir: Record<string, string> = { Up: "↑", Down: "↓", Left: "←", Right: "→" };
    return dir[code.slice(5)] ?? code;
  }
  const named: Record<string, string> = {
    Escape: "Esc",
    Space: "Space",
    Enter: "Enter",
    Tab: "Tab",
    Backquote: "`",
    Minus: "-",
    Equal: "=",
    BracketLeft: "[",
    BracketRight: "]",
    Backslash: "\\",
    Semicolon: ";",
    Quote: "'",
    Comma: ",",
    Period: ".",
    Slash: "/",
    ShiftLeft: "Shift ซ้าย",
    ShiftRight: "Shift ขวา",
    ControlLeft: "Ctrl ซ้าย",
    ControlRight: "Ctrl ขวา",
    AltLeft: "Alt ซ้าย",
    AltRight: "Alt ขวา",
  };
  return named[code] ?? code;
}
